/** Native mesh mm -> fixed patient LPS mm, column vectors, row-major JSON. */
export type Point3 = [number, number, number];
export const identity = () => [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

export function transformPoint(t: number[], p: ArrayLike<number>): Point3 {
  return [0, 1, 2].map((r) => t[r * 4] * p[0] + t[r * 4 + 1] * p[1] + t[r * 4 + 2] * p[2] + t[r * 4 + 3]) as Point3;
}

export function columnMajor(t: number[]) {
  return Array.from({ length: 16 }, (_, i) => t[(i % 4) * 4 + Math.floor(i / 4)]);
}

export function validateRigid(t: number[], tolerance = 1e-6) {
  if (t.length !== 16 || t.some((n) => !Number.isFinite(n))) throw new Error('A finite 4×4 matrix is required.');
  if ([12, 13, 14, 15].some((i) => Math.abs(t[i] - (i === 15 ? 1 : 0)) > tolerance)) throw new Error('Invalid homogeneous last row.');
  for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) {
    const dot = [0, 1, 2].reduce((v, k) => v + t[k * 4 + i] * t[k * 4 + j], 0);
    if (Math.abs(dot - (i === j ? 1 : 0)) > tolerance) throw new Error('Rigid rotation must be orthonormal.');
  }
  const det = t[0] * (t[5] * t[10] - t[6] * t[9]) - t[1] * (t[4] * t[10] - t[6] * t[8]) + t[2] * (t[4] * t[9] - t[5] * t[8]);
  if (Math.abs(det - 1) > tolerance) throw new Error('Reflections and scaling are not rigid registration.');
  return t;
}

function nonCollinear(points: Point3[]) {
  const a = points[0];
  const b = points.find((p) => Math.hypot(...p.map((v, i) => v - a[i])) > 1e-8);
  if (!b) return false;
  const u = b.map((v, i) => v - a[i]);
  return points.some((p) => {
    const v = p.map((n, i) => n - a[i]);
    const cross = [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]];
    return Math.hypot(...cross) > 1e-8 * Math.hypot(...u) * Math.hypot(...v);
  });
}

/** Jacobi eigensolver for Horn's symmetric 4×4 quaternion matrix. */
function largestEigenvector(input: number[][]) {
  const a = input.map((r) => [...r]);
  const vectors = Array.from({ length: 4 }, (_, r) => Array.from({ length: 4 }, (_, c) => Number(r === c)));
  for (let sweep = 0; sweep < 80; sweep++) {
    let p = 0; let q = 1;
    for (let i = 0; i < 4; i++) for (let j = i + 1; j < 4; j++) if (Math.abs(a[i][j]) > Math.abs(a[p][q])) { p = i; q = j; }
    if (Math.abs(a[p][q]) < 1e-13) break;
    const angle = 0.5 * Math.atan2(2 * a[p][q], a[q][q] - a[p][p]);
    const c = Math.cos(angle); const s = Math.sin(angle);
    const pp = a[p][p]; const qq = a[q][q]; const pq = a[p][q];
    for (let k = 0; k < 4; k++) {
      if (k !== p && k !== q) {
        const kp = a[k][p]; const kq = a[k][q];
        a[k][p] = a[p][k] = c * kp - s * kq;
        a[k][q] = a[q][k] = s * kp + c * kq;
      }
      const vp = vectors[k][p]; const vq = vectors[k][q];
      vectors[k][p] = c * vp - s * vq; vectors[k][q] = s * vp + c * vq;
    }
    a[p][p] = c * c * pp - 2 * s * c * pq + s * s * qq;
    a[q][q] = s * s * pp + 2 * s * c * pq + c * c * qq;
    a[p][q] = a[q][p] = 0;
  }
  const index = a.reduce((best, r, i) => r[i] > a[best][best] ? i : best, 0);
  return vectors.map((r) => r[index]);
}

export function solveRigid(source: Point3[], target: Point3[]) {
  if (source.length !== target.length || source.length < 3) throw new Error('At least three corresponding pairs are required.');
  if ([...source, ...target].some((p) => p.length !== 3 || p.some((n) => !Number.isFinite(n)))) throw new Error('Landmarks must be finite 3D points.');
  if (!nonCollinear(source) || !nonCollinear(target)) throw new Error('Landmarks must not be collinear.');
  const center = (points: Point3[]) => [0, 1, 2].map((j) => points.reduce((s, p) => s + p[j], 0) / points.length) as Point3;
  const cs = center(source); const ct = center(target);
  const h = Array.from({ length: 3 }, () => [0, 0, 0]);
  source.forEach((p, k) => { for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) h[i][j] += (p[i] - cs[i]) * (target[k][j] - ct[j]); });
  const [[xx, xy, xz], [yx, yy, yz], [zx, zy, zz]] = h;
  const [w, x, y, z] = largestEigenvector([
    [xx + yy + zz, yz - zy, zx - xz, xy - yx],
    [yz - zy, xx - yy - zz, xy + yx, zx + xz],
    [zx - xz, xy + yx, -xx + yy - zz, yz + zy],
    [xy - yx, zx + xz, yz + zy, -xx - yy + zz],
  ]);
  const t = [1 - 2 * (y * y + z * z), 2 * (x * y - z * w), 2 * (x * z + y * w), 0,
    2 * (x * y + z * w), 1 - 2 * (x * x + z * z), 2 * (y * z - x * w), 0,
    2 * (x * z - y * w), 2 * (y * z + x * w), 1 - 2 * (x * x + y * y), 0, 0, 0, 0, 1];
  const rotated = transformPoint(t, cs);
  ct.forEach((n, i) => { t[i * 4 + 3] = n - rotated[i]; });
  validateRigid(t);
  const rmsMm = Math.sqrt(source.reduce((sum, p, i) => sum + transformPoint(t, p).reduce((s, v, j) => s + (v - target[i][j]) ** 2, 0), 0) / source.length);
  return { matrix: t, rmsMm };
}
